import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  ViewChild,
} from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { Permission } from '@app/enumerations/permission';
import { getDefaultRouteForRole, Role } from '@app/enumerations/role';
import { UserNotification } from '@app/model/userNotification';
import { AuthService } from '@app/services/auth.service';
import { LocalStorageService } from '@app/services/local-storage.service';
import { NotificationService } from '@app/services/notification.service';
import { RoleService } from '@app/services/role.service';
import { DropdownArrowComponent } from '@app/shared/dropdown-arrow/dropdown-arrow';
import { environment } from '@env/environment';

export interface NavItem {
  label: string;
  routerLink?: string;
  externalUrl?: string;
  icon?: string;
  roles?: Role[];
  authorizedEmails?: string[];
  permission?: Permission;
  subItems?: NavItem[];
  beta?: boolean;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
  roles?: Role[];
}

@Component({
  selector: 'app-dashboard-layout',
  standalone: true,
  imports: [DropdownArrowComponent, CommonModule, RouterOutlet, RouterModule],
  templateUrl: './dashboard-layout.component.html',
  styleUrl: './dashboard-layout.component.scss',
})
export class DashboardLayoutComponent implements AfterViewInit {
  @ViewChild('desktopNav') desktopNav?: ElementRef<HTMLElement>;
  @ViewChild('mobileNav') mobileNav?: ElementRef<HTMLElement>;

  hasMoreNavItems = false;

  private authService = inject(AuthService);
  private localStorageService = inject(LocalStorageService);
  private notificationService = inject(NotificationService);
  private roleService = inject(RoleService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  userRole: Role | null = null;
  userEmail: string = '';
  consigneeName = '';
  isMobileView = window.innerWidth < 768;
  showNotifications = false;
  notifications: UserNotification[] = [];

  DEVELOPMENT_MODE = !environment.production;
  openedDropdowns: Record<string, boolean> = {};
  isNavOpen = false;

  base64UserObject: string | null = null;

  navigationGroups: NavGroup[] = [];

  constructor() {
    const jwt = this.authService.getUserFromJwt();
    if (!jwt) {
      this.router.navigate(['/auth']);
      return;
    }
    this.userRole = jwt.Role;

    if (this.DEVELOPMENT_MODE) {
      const roleOverride =
        this.localStorageService.getFromStorage('dev_role_override');
      if (roleOverride) {
        this.userRole = roleOverride as Role;
      }
    }

    this.userEmail = jwt.email || jwt.Email || '';
    this.consigneeName = jwt.unique_name?.split(' ')[0] || 'User';
    this.base64UserObject = btoa(
      JSON.stringify({
        email: jwt.email,
        name: jwt.unique_name,
        username: jwt.email?.split('@')[0],
      }),
    );

    this.navigationGroups = this.buildNavigationGroups();
  }

  ngAfterViewInit(): void {
    this.updateNavScrollIndicator();
  }

  onNavScroll(event: Event): void {
    this.updateNavScrollIndicator(event.target as HTMLElement);
  }

  private updateNavScrollIndicator(el?: HTMLElement): void {
    const target =
      el ?? this.desktopNav?.nativeElement ?? this.mobileNav?.nativeElement;
    if (!target) return;
    this.hasMoreNavItems =
      target.scrollHeight - target.scrollTop - target.clientHeight > 4;
  }

  isGroupVisible(group: NavGroup): boolean {
    if (!group.roles || group.roles.includes(this.userRole!)) return true;
    return group.items.some(
      (item) =>
        (item.permission || item.roles?.length) &&
        this.isItemAuthorized(item, group.roles),
    );
  }

  isItemAuthorized(item: NavItem, groupRoles?: Role[]): boolean {
    if (item.authorizedEmails?.length) {
      const hasEmail = item.authorizedEmails.some(
        (email) => email.toLowerCase() === this.userEmail.toLowerCase(),
      );
      if (hasEmail) return true;
    }

    const hasRole = item.roles?.includes(this.userRole!) ?? false;
    const hasGroupRole = groupRoles?.includes(this.userRole!) ?? false;
    const hasPermission = item.permission
      ? this.roleService.hasPermission(item.permission)
      : false;

    if (item.permission && item.roles?.length) {
      return hasPermission || hasRole;
    }

    if (item.permission) {
      return hasPermission || hasGroupRole;
    }

    if (item.roles?.length) {
      return hasRole;
    }

    return this.userRole != null;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobileView = window.innerWidth < 768;
    setTimeout(() => this.updateNavScrollIndicator());
  }
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const notificationButton = target.closest(
      'button[aria-label="Notifications"]',
    );
    const notificationDropdown = target.closest(
      '.absolute.right-0.mt-2.w-100.bg-white',
    );

    if (!notificationButton && !notificationDropdown) {
      this.showNotifications = false;
    }
  }

  buildNavigationGroups(): NavGroup[] {
    const baseGroups: NavGroup[] = [
      {
        title: 'Main',
        items: [
          {
            label: 'Dashboard',
            routerLink: '/dashboard',
            icon: 'dashboard.svg',
          },
          { label: 'Vehicles', routerLink: '/vehicles', icon: 'vehicle.svg' },
          {
            label: 'Vessel Schedule',
            routerLink: '/vessel-schedule',
            icon: 'vessel.svg',
          },
          {
            label: 'Calculator',
            routerLink: '/calculator',
            icon: 'calculator.svg',
          },
        ],
        roles: [Role.User, Role.Consignee, Role.SuperAdmin],
      },
      {
        title: this.getDynamicTitle(),
        items: [
          {
            label: 'Vehicles',
            routerLink: '/vehicles/list',
            icon: 'vehicle.svg',
            permission: Permission.ViewVehicles,
            roles: [
              Role.Admin,
              Role.SuperAdmin,
              Role.Logistic,
              Role.CustomerService,
              Role.Accounts,
              Role.Warehouse,
              Role.WarehouseAssistant,
              Role.Exporter,
              Role.Terminal,
            ],
          },
          {
            label: 'Customers',
            icon: 'user.svg',
            subItems: [
              {
                label: 'Customers',
                routerLink: '/customers',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewCustomers,
                roles: [
                  Role.Admin,
                  Role.SuperAdmin,
                  Role.Accounts,
                  Role.CustomerService,
                  Role.Logistic,
                ],
              },
              {
                label: 'Consignee Contacts',
                routerLink: '/consignee-contacts',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewConsigneeContacts,
                roles: [
                  Role.Admin,
                  Role.SuperAdmin,
                  Role.CustomerService,
                  Role.Accounts,
                ],
              },
            ],
          },
          // {
          //   label: 'Authorizations',
          //   externalUrl: `${environment.authorizationUrl}oauth/${this.base64UserObject}`,
          //   icon: 'solar-user-block-broken.svg',
          //   roles: [
          //     Role.Admin,
          //     Role.SuperAdmin,
          //     Role.CustomerService,
          //     Role.Accounts,
          //     Role.Terminal,
          //   ],
          // },
          {
            label: 'Authorizations',
            routerLink: '/authorizes',
            icon: 'solar-user-block-broken.svg',
            permission: Permission.ViewAuthorizations,
            roles: [
              Role.Admin,
              Role.SuperAdmin,
              Role.CustomerService,
              Role.Accounts,
              Role.Terminal,
            ],
          },
          {
            label: 'Auto Parts',
            routerLink: '/auto-parts',
            icon: 'autopart.svg',
            permission: Permission.ViewAutoparts,
            roles: [
              Role.Admin,
              Role.SuperAdmin,
              Role.Logistic,
              Role.CustomerService,
              Role.Accounts,
              Role.Warehouse,
              Role.WarehouseAssistant,
              Role.Terminal,
            ],
          },
          {
            label: 'Containers',
            routerLink: '/containers',
            icon: 'shipping-container-light.svg',
            subItems: [
              {
                label: 'Containers List',
                routerLink: '/containers',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewContainers,
              },
              {
                label: 'Container (Damaged)',
                routerLink: '/containers/damaged',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewDamagedContainers,
                roles: [
                  Role.Admin,
                  Role.SuperAdmin,
                  Role.Terminal,
                  Role.Logistic,
                  Role.CustomerService,
                  Role.Accounts,
                ],
              },
            ],
            roles: [
              Role.Admin,
              Role.SuperAdmin,
              Role.Terminal,
              Role.Logistic,
              Role.CustomerService,
              Role.Accounts,
            ],
          },
          {
            label: 'Vessels',
            routerLink: '/vessels',
            icon: 'vessel.svg',
            subItems: [
              {
                label: 'Vessels List',
                routerLink: '/vessels',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewVessels,
              },
              {
                label: 'Vessel Schedule',
                routerLink: '/vessels/vessel-schedule',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewVesselSchedule,
              },
            ],
            roles: [
              Role.Admin,
              Role.SuperAdmin,
              Role.Logistic,
              Role.CustomerService,
              Role.Accounts,
              Role.Exporter,
              Role.Terminal,
            ],
          },
          {
            label: 'Calculator',
            routerLink: '/calculator-internal',
            icon: 'calculator.svg',
            permission: Permission.ViewInternalCalculator,
            roles: [
              Role.Admin,
              Role.SuperAdmin,
              Role.Logistic,
              Role.CustomerService,
              Role.Accounts,
              Role.Terminal,
            ],
          },
        ],
        roles: [
          Role.Admin,
          Role.User,
          Role.SuperAdmin,
          Role.Logistic,
          Role.CustomerService,
          Role.Warehouse,
          Role.WarehouseAssistant,
          Role.Accounts,
          Role.Exporter,
          Role.Terminal,
        ],
      },
      {
        title: 'Utilities',
        items: [
          {
            label: 'Exchange Rates',
            routerLink: '/utilities/exchange-rates',
            icon: 'money-rounded.svg',
            permission: Permission.ViewExchangeRates,
            roles: [
              Role.Admin,
              Role.SuperAdmin,
              Role.CustomerService,
              Role.Accounts,
              Role.Logistic,
            ],
          },
          {
            label: 'Fee Types',
            routerLink: '/utilities/fee-types',
            icon: 'money-rounded.svg',
            roles: [Role.Accounts, Role.SuperAdmin],
          },
          {
            label: 'Send Documents',
            routerLink: '/utilities/send-documents',
            icon: 'email-new.svg',
            permission: Permission.SendDocuments,
            roles: [Role.Admin, Role.SuperAdmin, Role.CustomerService],
          },
          {
            label: 'Custom Email',
            routerLink: '/utilities/custom-email',
            icon: 'email-new.svg',
            permission: Permission.SendCustomEmail,
            roles: [Role.Admin, Role.SuperAdmin, Role.CustomerService],
          },
          {
            label: 'Batch Upload',
            icon: 'cloud-upload-bold-duotone.svg',
            subItems: [
              {
                label: 'Customer Id Scans',
                routerLink: '/batch-upload/customer-id-scans',
                icon: 'mdi-dot.svg',
                permission: Permission.UploadCustomerIdScans,
                roles: [Role.Admin, Role.SuperAdmin, Role.CustomerService],
              },
              {
                label: 'Terminal Documents',
                routerLink: '/batch-upload/terminal-documents',
                icon: 'mdi-dot.svg',
                permission: Permission.UploadTerminalDocuments,
                roles: [Role.Admin, Role.SuperAdmin, Role.Terminal],
              },
            ],
          },
        ],
        roles: [
          Role.Admin,
          Role.SuperAdmin,
          Role.Logistic,
          Role.CustomerService,
          Role.Accounts,
          Role.Terminal,
        ],
      },
      {
        title: 'Reports',
        items: [
          {
            label: 'Reports',
            icon: 'report-bell-curve.svg',
            subItems: [
              {
                label: 'Docs Collected',
                routerLink: '/reports/docs-collected',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewDocsCollectedReport,
              },
              {
                label: 'Amendments',
                routerLink: '/reports/amendments',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewAmendmentsReport,
              },
              {
                label: 'NDJ Charges',
                routerLink: '/reports/ndj-charges',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewNdjChargesReport,
                roles: [
                  Role.Admin,
                  Role.SuperAdmin,
                  Role.Accounts,
                  Role.Exporter,
                  Role.Terminal,
                ],
              },
              {
                label: 'SEZ Fees',
                routerLink: '/reports/sez-fees',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewSezFeesReport,
                roles: [Role.Admin, Role.SuperAdmin, Role.Accounts],
              },
              {
                label: 'Waived Days',
                routerLink: '/reports/waived-days',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewWaivedDaysReport,
                roles: [Role.Admin, Role.SuperAdmin, Role.Accounts],
              },
              {
                label: 'Payments',
                routerLink: '/reports/payments',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewPaymentsReport,
                roles: [Role.Admin, Role.SuperAdmin, Role.Accounts],
                authorizedEmails: ['venesa@atcfreight.com'],
              },
              {
                label: 'C&F Payments',
                routerLink: '/reports/cf-payments',
                icon: 'mdi-dot.svg',
                permission: Permission.ViewCfPaymentsReport,
                roles: [
                  Role.Admin,
                  Role.SuperAdmin,
                  Role.Accounts,
                  Role.Exporter,
                ],
              },
            ],
          },
        ],
        roles: [
          Role.Admin,
          Role.User,
          Role.SuperAdmin,
          Role.Logistic,
          Role.CustomerService,
          Role.Accounts,
          Role.Exporter,
          Role.Terminal,
        ],
      },
      {
        title: 'Admin',
        items: [
          {
            label: 'Consignees',
            routerLink: '/consignees',
            icon: 'user.svg',
            permission: Permission.ViewConsignees,
            roles: [Role.Admin, Role.SuperAdmin],
          },
          {
            label: 'Users',
            routerLink: '/users',
            icon: 'user.svg',
            permission: Permission.ViewUsers,
            roles: [Role.Admin, Role.SuperAdmin],
          },
        ],
        roles: [Role.Admin, Role.SuperAdmin],
      },
    ];

    if (this.DEVELOPMENT_MODE) {
      baseGroups.push({
        title: 'Development',
        items: [
          {
            label: 'Dev Tools',
            routerLink: '/dev-tools',
            icon: 'settings-broken.svg',
          },
        ],
        roles: [Role.Admin, Role.SuperAdmin],
      });
    }

    return baseGroups.map((group) => ({
      ...group,
      items: group.items
        .filter((item) => this.isItemAuthorized(item, group.roles))
        .map((item) => ({
          ...item,
          subItems: item.subItems?.filter((subItem) =>
            this.isItemAuthorized(subItem, item.roles ?? group.roles),
          ),
        }))
        .filter((item) => !item.subItems || item.subItems.length > 0),
    }));
  }

  toggleDropdown(item: NavItem): void {
    if (item.subItems?.length) {
      this.openedDropdowns[item.label] = !this.openedDropdowns[item.label];
      setTimeout(() => this.updateNavScrollIndicator());
    }
  }

  logout(): void {
    this.authService.logout();
  }

  openProfile(): void {
    this.router.navigate(['/profile']);
  }

  navigateToHome(): void {
    this.router.navigate([getDefaultRouteForRole(this.userRole ?? '')]);
  }

  toggleNotifications(): void {
    this.showNotifications = !this.showNotifications;
  }

  fetchNotifications(): void {
    this.notificationService
      .getUserNotificationsByUser(1000, 1)
      .subscribe((notifications) => {
        this.notifications = notifications;
      });
  }

  markAsRead(notification: UserNotification): void {}

  markAll(): void {}

  get hasNotificationsCount(): boolean {
    return this.notifications.some((n) => !n.isRead);
  }

  getYear(): number {
    return new Date().getFullYear();
  }

  getDynamicTitle(): string {
    const titles: Record<Role, string> = {
      [Role.Admin]: 'Admin',
      [Role.Consignee]: 'Consignee',
      [Role.Exporter]: 'Exporter',
      [Role.Guest]: 'Guest',
      [Role.Logistic]: 'Logistic',
      [Role.CustomerService]: 'Customer Service',
      [Role.Warehouse]: 'Warehouse',
      [Role.WarehouseAssistant]: 'Warehouse Assistant',
      [Role.Accounts]: 'Accounts',
      [Role.SuperAdmin]: 'Super Admin',
      [Role.User]: 'User',
      [Role.Terminal]: 'Terminal',
    };
    return titles[this.userRole!] || 'Menu';
  }

  isActiveLink(link?: string): boolean {
    if (!link) return false;
    const urlTree = this.router.createUrlTree([link]);
    return this.router.isActive(urlTree, {
      paths: 'subset',
      queryParams: 'exact',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  isActiveLinkDropDown(link?: string): boolean {
    if (!link) return false;
    const urlTree = this.router.createUrlTree([link]);
    return this.router.isActive(urlTree, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  trackByLabel(index: number, item: { label: string }): string {
    return item.label;
  }

  trackByGroupTitle(index: number, group: { title: string }): string {
    return group.title;
  }
}
